import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["section"]
  static values = { 
    contractType: String,
    showWhen: String,
    isPersisted: Boolean,
    quoteId: String
  }

  connect() {
    // Initial state setzen
    this.toggleVisibility()

    // Event-Listener für Contract-Type-Änderungen hinzufügen
    const contractTypeSelect = this.element.querySelector('select[name*="[contract_type]"]')
    if (contractTypeSelect) {
      // Speichere den ursprünglichen Wert für Warnung (nur bei persistierten Datensätzen)
      if (this.isPersistedValue) {
        this.originalContractType = contractTypeSelect.value
      }
      
      contractTypeSelect.addEventListener('change', (event) => {
        if (this.isPersistedValue) {
          this.handleContractTypeChange(event)
        } else {
          // Bei neuen Datensätzen einfach Sichtbarkeit aktualisieren
          this.toggleVisibility()
        }
        // Aktualisiere originalContractType nach jedem Wechsel
        this.originalContractType = event.target.value
      })
    }
  }

  toggle() {
    this.toggleVisibility()
  }

  handleContractTypeChange(event) {
    const newContractType = event.target.value
    const oldContractType = this.originalContractType

    // Prüfe ob eine Warnung nötig ist
    if (this.shouldShowWarning(oldContractType, newContractType)) {
      this.showWarning(oldContractType, newContractType, event.target)
    } else {
      this.toggleVisibility()
    }
  }

  shouldShowWarning(oldType, newType) {
    // Warnung nur anzeigen wenn:
    // 1. Datensatz ist persistiert (isPersistedValue = true)
    // 2. Es wechselt zwischen verschiedenen Vertragsarten
    if (!this.isPersistedValue || !oldType || oldType === '' || oldType === newType) {
      return false
    }
    
    const hasLeasingData = this.hasLeasingSpecificData()
    const hasServiceContractData = this.hasServiceContractSpecificData()
    const hasPlacementData = this.hasPlacementSpecificData()
    const hasPermanentData = this.hasPermanentSpecificData()
    
    // Warnung bei Wechsel von Leasing weg
    if (hasLeasingData && oldType === 'leasing' && newType !== 'leasing') {
      return true
    }
    
    // Warnung bei Wechsel zu Leasing
    if (hasLeasingData && oldType !== 'leasing' && newType === 'leasing') {
      return true
    }
    
    // Warnung wenn Leasing-Daten vorhanden sind und zu einer anderen Vertragsart gewechselt wird
    if (hasLeasingData && newType !== 'leasing') {
      return true
    }
    
    // Warnung bei Wechsel von/zu Dienstvertrag
    if (hasServiceContractData && (oldType === 'service_contract' || newType === 'service_contract')) return true
    
    // Warnung bei Wechsel von/zu Vermittlung
    if (hasPlacementData && (oldType === 'placement' || newType === 'placement')) return true
    
    // Warnung bei Wechsel von/zu Festanstellung
    if (hasPermanentData && (oldType === 'permanent' || newType === 'permanent')) return true
    
    return false
  }

  getQuoteId() {
    // Verwende die quoteId Value aus dem Stimulus Controller
    return this.quoteIdValue || 'new'
  }

  hasLeasingSpecificData() {
    // Prüfe ob Equal Pay, Höchstüberlassungsdauer oder Arbeitszeit gesetzt sind
    const quoteId = this.getQuoteId()
    
    // Equal Pay Checkbox prüfen
    const equalPayCheckbox = document.getElementById(`quote_${quoteId}_equal_pay`)
    const equalPaySet = equalPayCheckbox && equalPayCheckbox.checked
    
    // Höchstüberlassungsdauer prüfen
    const maxDurationInput = document.getElementById(`quote_${quoteId}_max_duration_months`)
    const maxDurationSet = maxDurationInput && maxDurationInput.value && maxDurationInput.value !== ''
    
    // Wöchentliche Arbeitszeit prüfen
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    const workingHoursSet = workingHoursInput && workingHoursInput.value && workingHoursInput.value !== ''
    
    
    return equalPaySet || maxDurationSet || workingHoursSet
  }

  hasServiceContractSpecificData() {
    // Prüfe ob Dienstvertrag-spezifische Felder gesetzt sind
    const quoteId = this.getQuoteId()
    const dailyRateInput = document.getElementById(`quote_${quoteId}_daily_rate`)
    const totalVolumeInput = document.getElementById(`quote_${quoteId}_total_volume`)
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    
    const dailyRateSet = dailyRateInput && dailyRateInput.value && dailyRateInput.value !== ''
    const totalVolumeSet = totalVolumeInput && totalVolumeInput.value && totalVolumeInput.value !== ''
    const workingHoursSet = workingHoursInput && workingHoursInput.value && workingHoursInput.value !== ''
    
    return dailyRateSet || totalVolumeSet || workingHoursSet
  }

  hasPlacementSpecificData() {
    // Prüfe ob Vermittlung-spezifische Felder gesetzt sind
    const quoteId = this.getQuoteId()
    const remunerationInput = document.getElementById(`quote_${quoteId}_remuneration`)
    const remunerationConditionsInput = document.getElementById(`quote_${quoteId}_remuneration_conditions`)
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    
    const remunerationSet = remunerationInput && remunerationInput.value && remunerationInput.value !== ''
    const remunerationConditionsSet = remunerationConditionsInput && remunerationConditionsInput.value && remunerationConditionsInput.value !== ''
    const workingHoursSet = workingHoursInput && workingHoursInput.value && workingHoursInput.value !== ''
    
    return remunerationSet || remunerationConditionsSet || workingHoursSet
  }

  hasPermanentSpecificData() {
    // Prüfe ob Festanstellung-spezifische Felder gesetzt sind
    const quoteId = this.getQuoteId()
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    
    const workingHoursSet = workingHoursInput && workingHoursInput.value && workingHoursInput.value !== ''
    
    return workingHoursSet
  }

  showWarning(oldType, newType, selectElement) {
    const contractTypeNames = {
      'leasing': 'Arbeitnehmerüberlassung',
      'service_contract': 'Dienstvertrag',
      'placement': 'Direktvermittlung',
      'permanent': 'Intern'
    }

    const oldName = contractTypeNames[oldType] || oldType
    const newName = contractTypeNames[newType] || newType

    const message = this.buildWarningMessage(oldName, newName)
    
    if (confirm(message)) {
      // Benutzer bestätigt - Felder zurücksetzen basierend auf alter und neuer Vertragsart
      this.resetFieldsBasedOnContractTypes(oldType, newType)
      this.toggleVisibility()
    } else {
      // Benutzer bricht ab - Contract Type zurücksetzen
      selectElement.value = oldType
      this.toggleVisibility()
    }
  }

  buildWarningMessage(oldName, newName) {
    return `⚠️ Vertragsart wechseln von "${oldName}" zu "${newName}"\n\n` +
           `Dies wird folgende Auswirkungen haben:\n\n` +
           `• Vertragsart-spezifische Felder werden zurückgesetzt\n` +
           `• Nur relevante Felder für die neue Vertragsart bleiben erhalten\n\n` +
           `Möchten Sie fortfahren?`
  }

  resetFieldsBasedOnContractTypes(oldType, newType) {
    // Zurücksetzen basierend auf der alten Vertragsart
    if (oldType === 'leasing') {
      this.resetLeasingSpecificFields()
    } else if (oldType === 'service_contract') {
      this.resetServiceContractSpecificFields()
    } else if (oldType === 'placement') {
      this.resetPlacementSpecificFields()
    } else if (oldType === 'permanent') {
      this.resetPermanentSpecificFields()
    }
    
    // Zusätzlich: Zurücksetzen basierend auf der neuen Vertragsart
    // Wenn die neue Vertragsart bestimmte Felder nicht unterstützt
    if (newType !== 'leasing') {
      this.resetLeasingSpecificFields()
    }
    if (newType !== 'service_contract') {
      this.resetServiceContractSpecificFields()
    }
    if (newType !== 'placement') {
      this.resetPlacementSpecificFields()
    }
    if (newType !== 'permanent') {
      this.resetPermanentSpecificFields()
    }
    
    // Spezielle Behandlung für Leasing-Wechsel
    if (oldType === 'leasing' && newType !== 'leasing') {
      this.resetLeasingSpecificFields()
    }
  }

  resetLeasingSpecificFields() {
    const quoteId = this.getQuoteId()
    
    // Equal Pay zurücksetzen - auch wenn nicht sichtbar
    const equalPayCheckbox = document.getElementById(`quote_${quoteId}_equal_pay`)
    if (equalPayCheckbox) {
      equalPayCheckbox.checked = false
      equalPayCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Höchstüberlassungsdauer zurücksetzen - auch wenn nicht sichtbar
    const maxDurationInput = document.getElementById(`quote_${quoteId}_max_duration_months`)
    if (maxDurationInput) {
      maxDurationInput.value = ''
      maxDurationInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Wöchentliche Arbeitszeit zurücksetzen - auch wenn nicht sichtbar
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    if (workingHoursInput) {
      workingHoursInput.value = ''
      workingHoursInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  resetServiceContractSpecificFields() {
    const quoteId = this.getQuoteId()
    
    // Tagessatz zurücksetzen
    const dailyRateInput = document.getElementById(`quote_${quoteId}_daily_rate`)
    if (dailyRateInput) {
      dailyRateInput.value = ''
      dailyRateInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Gesamtvolumen zurücksetzen
    const totalVolumeInput = document.getElementById(`quote_${quoteId}_total_volume`)
    if (totalVolumeInput) {
      totalVolumeInput.value = ''
      totalVolumeInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Wöchentliche Arbeitszeit zurücksetzen
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    if (workingHoursInput) {
      workingHoursInput.value = ''
      workingHoursInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  resetPlacementSpecificFields() {
    const quoteId = this.getQuoteId()
    
    // Honorar zurücksetzen
    const remunerationInput = document.getElementById(`quote_${quoteId}_remuneration`)
    if (remunerationInput) {
      remunerationInput.value = ''
      remunerationInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Honorarbedingungen zurücksetzen
    const remunerationConditionsInput = document.getElementById(`quote_${quoteId}_remuneration_conditions`)
    if (remunerationConditionsInput) {
      remunerationConditionsInput.value = ''
      remunerationConditionsInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Wöchentliche Arbeitszeit zurücksetzen
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    if (workingHoursInput) {
      workingHoursInput.value = ''
      workingHoursInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  resetPermanentSpecificFields() {
    const quoteId = this.getQuoteId()
    
    // Wöchentliche Arbeitszeit zurücksetzen
    const workingHoursInput = document.getElementById(`quote_${quoteId}_working_hours`)
    if (workingHoursInput) {
      workingHoursInput.value = ''
      workingHoursInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  toggleVisibility() {
    const contractTypeSelect = this.element.querySelector('select[name*="[contract_type]"]')
    if (!contractTypeSelect) return

    const selectedValue = contractTypeSelect.value
    const sections = document.querySelectorAll('[data-contract-type-visibility]')

    sections.forEach(section => {
      const sectionContractType = section.dataset.contractType
      const showWhen = section.dataset.showWhen
      
      // Unterstütze mehrere Vertragsarten durch Leerzeichen getrennt
      const allowedContractTypes = sectionContractType ? sectionContractType.split(' ') : []
      const isMatching = allowedContractTypes.includes(selectedValue)

      // display:'' (statt 'block') entfernt inline-style komplett, damit das
      // urspruengliche CSS (z.B. md:grid) wieder greift — sonst stacken
      // grid-Sections vertikal.
      if (isMatching) {
        if (showWhen === 'not') {
          section.style.display = 'none'
        } else if (showWhen === 'only') {
          section.style.display = ''
        }
      } else {
        if (showWhen === 'not') {
          section.style.display = ''
        } else if (showWhen === 'only') {
          section.style.display = 'none'
        }
      }
    })
  }
}
